import { AssignmentBasicModel, AssignmentModel, AssignmentExtendedModel, AssignmentBasicTitleModel, AssignmentWithExternalDataModel, AssignmentTitleMapped, AssignmentBasicExtendedModel, AssignmentSimplifiedModel, AssignmentBasicConfirmedModel } from "@shared/models";
import { ExternalEmployee, EBSListItem, CustomerEmailListItem, EBS } from "@shared/factories";
import { FormatDatesService } from "@shared/services";

export class AssignmentBasicTitle {
  id:     number;
  title:  string;
  mapped: AssignmentTitleMapped;
  constructor(data: AssignmentBasicTitleModel) {
    this.id     = data.id    ? data.id    : null;
    this.title  = data.title ? data.title : null;
    this.mapped = this.mapAssignmentTitle(data.title);
  }

  toJSON(): AssignmentBasicTitleModel {
    return {
      id:    this.id    ? this.id    : null,
      title: this.title ? this.title : null,
    };
  }

  private mapAssignmentTitle(title: string): AssignmentTitleMapped {
    let date         = '(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}';
    let isDateString = new RegExp(`^${date}\s*-\s*${date}`);
    if (!isDateString.test(title)) return null;

    let start = title?.split('-')[0]?.replace(' ', '');
    let end   = title?.split('-')[1]?.split(' ')?.filter(t => t)[0];
    if (!start || !end) return null;

    let dates = `${start} - ${end}`;
    let meta  = title.replace(dates+' ', '').replace(dates.replace(' ',''), '');

    if (!dates.replace(' ', '').replace('-','').length) return null;
    if (!meta.replace(' ', '').replace('-','').length ) return null;

    return { dates, meta };
  }

}

export class AssignmentWithExternalData extends AssignmentBasicTitle {
  personal_number:   number;
  customer_location: number;
  constructor(data: AssignmentWithExternalDataModel) {
    super(data);

    this.personal_number   = data.personal_number   ? data.personal_number   : null;
    this.customer_location = data.customer_location ? data.customer_location : null;
  }

  toJSON(): AssignmentWithExternalDataModel {
    return Object.assign(super.toJSON(), {
      personal_number:   this.personal_number   ? this.personal_number   : null,
      customer_location: this.customer_location ? this.customer_location : null
    });
  }

}

export class AssignmentBasic extends AssignmentBasicTitle {
  project:       string;
  prefilProject: boolean;
  constructor(data: AssignmentBasicModel) {
    super(data);
    this.project       = data.project                                ? data.project                       : null;
    this.prefilProject = data.prefill_activity_with_project !== null ? data.prefill_activity_with_project : null;
  }

  toJSON(): AssignmentBasicModel {
    return Object.assign(super.toJSON(), {
      project:                       this.project                ? this.project       : null,
      prefill_activity_with_project: this.prefilProject !== null ? this.prefilProject : null,
    });
  }

}

export class AssignmentBasicConfirmed extends AssignmentBasic {
  confirmed: boolean;
  constructor(data: AssignmentBasicConfirmedModel) {
    super(data);
    this.confirmed = data.confirmed !== undefined ? data.confirmed : null;
  }

  toJSON(): AssignmentBasicConfirmedModel {
    return Object.assign(super.toJSON(), {
      confirmed: this.confirmed !== undefined && this.confirmed !== null ? this.confirmed : null,
    });
  }

}

export class AssignmentBasicExtended extends AssignmentBasicConfirmed {
  active:    boolean;
  starts_at: Date;
  ends_at:   Date;
  constructor(data: AssignmentBasicExtendedModel) {
    super(data);
    this.active    = data.active !== undefined ? data.active : null;
    this.starts_at = FormatDatesService.parseDate(data.starts_at);
    this.ends_at   = FormatDatesService.parseDate(data.ends_at);
  }

  toJSON(): AssignmentBasicExtendedModel {
    return Object.assign(super.toJSON(), {
      active:    this.active !== undefined && this.active !== null ? this.active : null,
      starts_at: FormatDatesService.parseDateISO(this.starts_at),
      ends_at:   FormatDatesService.parseDateISO(this.ends_at),
    });
  }

}

export class AssignmentSimplified extends AssignmentBasicExtended {
  customer_location: number;
  lastEbsVersion:    EBS;
  constructor(data: AssignmentSimplifiedModel) {
    super(data);
    this.customer_location = data.customer_location ? +data.customer_location        : null;
    this.lastEbsVersion    = data.last_ebs_version  ? new EBS(data.last_ebs_version) : null;
  }

  toJSON(): AssignmentSimplifiedModel {
    return Object.assign(super.toJSON(), {
      customer_location: this.customer_location ? this.customer_location       : null,
      last_ebs_version:  this.lastEbsVersion    ? this.lastEbsVersion.toJSON() : null
    });
  }

}

export class Assignment extends AssignmentBasicExtended {
  created_at:        Date;
  equal_pay:         Date;
  max_rental_period: Date;
  keyContact:        CustomerEmailListItem;
  constructor(data: AssignmentModel) {
    super(data);
    this.created_at        = FormatDatesService.parseDate(data.created_at);
    this.equal_pay         = FormatDatesService.parseDate(data.equal_pay);
    this.max_rental_period = FormatDatesService.parseDate(data.max_rental_period);
    this.keyContact        = data.key_contact_customer ? new CustomerEmailListItem(data.key_contact_customer) : null;
  }

  toJSON(): AssignmentModel {
    return Object.assign(super.toJSON(), {
      created_at:           FormatDatesService.parseDateISO(this.created_at),
      equal_pay:            FormatDatesService.parseDateISO(this.equal_pay),
      max_rental_period:    FormatDatesService.parseDateISO(this.max_rental_period),
      key_contact_customer: this.keyContact ? this.keyContact.toJSON() : null
    });
  }
}

export class AssignmentExtended extends Assignment {
  personal_number:   number;
  customer_location: number;
  external_employee: ExternalEmployee;
  ebs_data_list:     EBSListItem[];

  stateISO:          string;
  constructor(data: AssignmentExtendedModel) {
    super(data);
    this.personal_number   = data.personal_number       ? +data.personal_number                           : null;
    this.customer_location = data.customer_location     ? +data.customer_location                         : null;
    this.external_employee = data.external_employee     ? new ExternalEmployee(data.external_employee)    : null;
    this.ebs_data_list     = data.ebs_data_list?.length ? data.ebs_data_list.map(e => new EBSListItem(e)) : [];
    this.stateISO          = data.state_iso;
  }

  get latestEBS(): EBSListItem {
    return this.ebs_data_list.find(e => e.version === Math.max(...this.ebs_data_list.map(ee => ee.version)));
  }

  get history(): EBSListItem[] {
    return this.ebs_data_list.filter(e => e.id !== this.latestEBS.id);
  }

  toJSON(): AssignmentExtendedModel {
    return Object.assign(super.toJSON(), {
      personal_number:   this.personal_number       ? this.personal_number                    : null,
      customer_location: this.customer_location     ? this.customer_location                  : null,
      external_employee: this.external_employee     ? this.external_employee.toJSON()         : null,
      ebs_data_list:     this.ebs_data_list?.length ? this.ebs_data_list.map(e => e.toJSON()) : [],
      state_iso:         this.stateISO              ? this.stateISO                           : null,
    });
  }
}
 